import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { goodsTransfers, type GoodsTransferListItem } from '@/api/goodsTransfers';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

export default function GoodsTransfersPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['goods-transfers'],
    queryFn: () => goodsTransfers.list({ limit: 200 }),
  });
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgId = orgList.data?.items[0]?.id;
  const warehousesQ = useQuery({
    queryKey: ['warehouses', orgId],
    queryFn: () => refs.warehouses(orgId!),
    enabled: !!orgId,
  });

  const whName = useMemo(() => {
    const m = new Map<string, string>();
    warehousesQ.data?.forEach((w) => m.set(w.id, `${w.code} — ${w.name}`));
    return m;
  }, [warehousesQ.data]);

  const cols = useMemo<ColumnDef<GoodsTransferListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'warehouse_from_id', header: 'Зі складу',
      sortValue: (r) => whName.get(r.warehouse_from_id) ?? '',
      cell: (r) => whName.get(r.warehouse_from_id) ?? '',
    },
    {
      key: 'warehouse_to_id', header: 'На склад',
      sortValue: (r) => whName.get(r.warehouse_to_id) ?? '',
      cell: (r) => whName.get(r.warehouse_to_id) ?? '',
    },
    {
      key: 'total_quantity', header: 'Кількість', width: 130, align: 'right',
      sortValue: (r) => Number(r.total_quantity),
      cell: (r) => Number(r.total_quantity).toLocaleString('uk-UA',
        { minimumFractionDigits: 0, maximumFractionDigits: 4 }),
    },
    {
      key: 'total_amount', header: 'Сума (собівартість)', width: 180, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => Number(r.total_amount).toLocaleString('uk-UA',
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      key: 'posted', header: 'Проведено', width: 130, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => (r.posted ? '✓ так' : 'ні'),
    },
  ], [whName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Переміщення товарів</h2>
        <button className="primary" onClick={() => navigate('/goods-transfers/new')}>
          + Нове переміщення
        </button>
      </div>
      <DataTable<GoodsTransferListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/goods-transfers/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
